
import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenAI, Type } from "@google/genai";
import html2canvas from 'html2canvas';

interface RecommendationsStepProps {
  t: any;
  isRTL: boolean;
  onBack: () => void;
  onReset: () => void;
  onHome: () => void;
  onExploreAi?: () => void;
  userChoices: any;
  language: string;
  onRecommendationsReady?: (preferredCity: string) => void;
}

const RecommendationsStep: React.FC<RecommendationsStepProps> = ({ t, isRTL, onBack, onReset, onHome, onExploreAi, userChoices, language, onRecommendationsReady }) => {
  const [loading, setLoading] = useState(true);
  const [recommendation, setRecommendation] = useState<any>(null);
  const [error, setError] = useState<boolean>(false);
  const [sharing, setSharing] = useState(false);
  const [showExploreBtn, setShowExploreBtn] = useState(false);
  
  const captureRef = useRef<HTMLDivElement>(null);
  const scrollEndRef = useRef<HTMLDivElement>(null);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(false);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const langName = language === 'zh' ? 'Chinese' : 'English';
      const hasChildren = userChoices.familySize === 'family_with_children';
      const wantsBuy = userChoices.financial.planType === 'buy';
      const wantsRent = userChoices.financial.planType === 'rent';
      const hasSpecialNeeds = userChoices.specialNeeds;

      // Constructing specific instructions for special needs
      let specialNeedsPrompt = "";
      if (hasSpecialNeeds && userChoices.specialNeedsDetail) {
        const { category, eduPath } = userChoices.specialNeedsDetail;
        specialNeedsPrompt = `SPECIALIZED REQUIREMENT: User requires support for ${category === 'neuro' ? 'Neurodiversity' : 'Physical Disability'}. 
        PATH: ${eduPath === 'k12' ? 'K-12 Educational Support (IEP/504)' : '18+ Vocational & Life Skills Transition'}.
        CRITICAL: The recommended city MUST have top-tier infrastructure for this. If path is K-12, detail the city's special education excellence. If path is 18+, detail vocational training and community transition programs.`;
      }

      const prompt = `Recommend exactly ONE best US city for relocation based on weighted user preferences.
        USER PROFILE:
        - Profession: ${userChoices.profession}
        - Family Structure: ${userChoices.familySize}
        - Financial Plan: ${userChoices.financial.planType} within ${userChoices.financial.range} range
        - Climate Preferences: ${userChoices.climate.join(', ') || 'Flexible'}
        ${specialNeedsPrompt}

        ANALYSIS BLOCKS: 
        1. Career & Economic Hub (Industry: ${userChoices.profession})
        2. ${hasChildren ? 'Education & Schools (Include Specific Support if requested),' : 'Living & Lifestyle,'}
        3. ${wantsBuy ? 'Real Estate Dynamics (Buying),' : 'Rental Market Dynamics,'}
        4. Environmental Profile (Climate)
        5. ${hasSpecialNeeds ? 'Specialized Support Infrastructure,' : 'Community & Culture'}

        Respond in ${langName}. Use high-quality professional consultation tone.
        Return the result as a JSON object with city, state, summary, and an array of cards (title, icon, content).
        Icons must be valid Material Symbols Outlined names (e.g., 'school', 'work', 'medical_services', 'accessible').`;

      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          responseMimeType: 'application/json',
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              city: { type: Type.STRING },
              state: { type: Type.STRING },
              summary: { type: Type.STRING },
              cards: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    title: { type: Type.STRING },
                    icon: { type: Type.STRING },
                    content: { type: Type.STRING }
                  },
                  required: ["title", "icon", "content"]
                }
              }
            },
            required: ["city", "state", "summary", "cards"]
          }
        }
      });

      const data = JSON.parse(response.text || "{}");
      setRecommendation(data);
      if (onRecommendationsReady) onRecommendationsReady(data.city);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(true);
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecommendations(); }, []);

  useEffect(() => {
    if (!loading && recommendation) {
      const observer = new IntersectionObserver(([e]) => e.isIntersecting && setShowExploreBtn(true), { threshold: 0.1 });
      if (scrollEndRef.current) observer.observe(scrollEndRef.current);
      return () => {
        if (scrollEndRef.current) observer.unobserve(scrollEndRef.current);
      };
    }
  }, [loading, recommendation]);

  const handleShare = async () => {
    if (!captureRef.current || sharing) return;
    setSharing(true);
    try {
      window.scrollTo(0, 0);

      const canvas = await html2canvas(captureRef.current, {
        useCORS: true,
        scale: 3, 
        backgroundColor: '#FFFFFF',
        logging: false,
        onclone: (documentClone) => {
          const area = documentClone.querySelector('#capture-area') as HTMLElement;
          if (area) {
            area.style.width = '450px'; 
            area.style.padding = '40px';
            area.style.margin = '0';
            area.style.borderRadius = '0';
            area.style.position = 'relative';
            area.style.left = '0';
            area.style.top = '0';
            area.style.transform = 'none';
          }
        }
      });

      canvas.toBlob(async (blob) => {
        if (!blob) { setSharing(false); return; }
        const file = new File([blob], `settlo-${recommendation.city}.png`, { type: 'image/png' });
        const shareData = {
          files: [file],
          title: `Settlo Strategy - ${recommendation.city}`,
          text: `My tailored relocation strategy for ${recommendation.city} by Settlo.`
        };

        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData).catch(() => {});
        } else {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `settlo-${recommendation.city}.png`;
          link.click();
          URL.revokeObjectURL(url);
        }
        setSharing(false);
      }, 'image/png', 1.0);
    } catch (err) {
      console.error("Share failed:", err);
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-10 text-center bg-white">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-10"></div>
        <div className="max-w-md space-y-4">
          <h2 className="text-[24px] font-bold text-slate-dark tracking-tight leading-tight">{t.loadingTitle}</h2>
          <p className="text-slate-soft text-[15px] leading-relaxed opacity-80">{t.loadingDesc1}</p>
          <div className="w-8 h-[2px] bg-slate-100 mx-auto"></div>
          <p className="text-slate-soft/60 text-[13px] leading-relaxed italic">{t.loadingDesc2}</p>
        </div>
      </div>
    );
  }

  if (error || !recommendation) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-12 text-center bg-white">
        <span className="material-symbols-outlined text-red-500 text-[48px] mb-4">error</span>
        <h2 className="text-[24px] font-bold text-slate-dark mb-4">{t.errorTitle}</h2>
        <button onClick={fetchRecommendations} className="bg-primary text-white font-bold py-3.5 px-10 rounded-stripe shadow-stripe-button">{t.errorBtn}</button>
      </div>
    );
  }

  return (
    <div className={`relative flex flex-col h-screen w-full max-w-2xl mx-auto bg-white overflow-hidden font-display ${isRTL ? 'text-right' : 'text-left'}`}>
      <div className="absolute inset-0 z-0 pointer-events-none opacity-40">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
      </div>

      <header className="relative w-full pt-8 px-8 md:px-12 pb-4 flex items-center justify-between z-20">
        <button 
          onClick={onHome}
          className={`flex items-center gap-2.5 transition-opacity hover:opacity-80 active:scale-95 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
        >
          <span className="material-symbols-outlined text-primary text-[28px] font-bold">flight_takeoff</span>
          <h2 className="text-slate-dark tracking-tighter text-[20px] font-black leading-none">Settlo</h2>
        </button>
        <button onClick={handleShare} disabled={sharing} className="w-11 h-11 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-soft hover:text-primary transition-all active:scale-95 disabled:opacity-50">
          <span className={`material-symbols-outlined text-[24px] ${sharing ? 'animate-pulse' : ''}`}>{sharing ? 'sync' : 'share'}</span>
        </button>
      </header>

      <main className="relative z-10 flex-1 overflow-y-auto no-scrollbar px-8 md:px-12 pb-24">
        <div ref={captureRef} id="capture-area" className="bg-white">
          <div className="mb-12 pt-6">
            <div className="text-slate-soft/40 text-[11px] font-[900] tracking-[0.3em] uppercase mb-5">{t.recStep}</div>
            <div className="mb-8">
              <h1 className="text-[42px] md:text-[58px] font-[900] leading-[1.0] tracking-tighter text-slate-dark mb-4">
                {recommendation.city}
                <span className="block text-[24px] text-primary opacity-90 mt-2">{recommendation.state}</span>
              </h1>
              <div className="w-16 h-1.5 bg-primary rounded-full mb-8"></div>
            </div>
            <p className="text-[17px] md:text-[20px] leading-[1.6] text-slate-soft font-medium">{recommendation.summary}</p>
          </div>

          <div className="space-y-6">
            {recommendation.cards.map((card: any, idx: number) => (
              <div key={idx} className="group p-7 md:p-9 bg-white border border-slate-100 rounded-stripe shadow-stripe-card hover:shadow-xl hover:border-primary/20 transition-all duration-500 overflow-hidden">
                <div className={`flex items-center gap-4 mb-5 relative z-10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300 flex-shrink-0">
                    <span className="material-symbols-outlined text-[26px] block font-normal leading-none" style={{ fontVariantLigatures: 'none' }}>{card.icon}</span>
                  </div>
                  <h3 className="text-[20px] font-[800] text-slate-dark tracking-tight leading-tight">{card.title}</h3>
                </div>
                <p className="text-[15px] md:text-[17px] leading-[1.65] text-slate-soft font-google-sans-text relative z-10">{card.content}</p>
              </div>
            ))}
          </div>
        </div>

        <div ref={scrollEndRef} className="h-4"></div>

        <div className={`mt-20 mb-10 transition-all duration-1000 transform ${showExploreBtn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12 pointer-events-none'}`}>
          <button onClick={onExploreAi} className="w-full relative flex items-center justify-center bg-primary text-white font-[900] text-[18px] py-5 rounded-stripe shadow-stripe-button transition-all duration-300 active:scale-[0.98]">
            <span>{t.exploreBtn}</span>
          </button>
        </div>
      </main>
    </div>
  );
};

export default RecommendationsStep;
